import type { CharacterGender, VoiceAssignment } from '@/types/domain';
import { hashString } from '@/lib/id';

/** Minimal, testable projection of `SpeechSynthesisVoice`. */
export interface VoiceOption {
  voiceURI: string;
  name: string;
  lang: string;
  localService: boolean;
  isDefault: boolean;
}

export function toVoiceOption(voice: SpeechSynthesisVoice): VoiceOption {
  return {
    voiceURI: voice.voiceURI,
    name: voice.name,
    lang: voice.lang,
    localService: voice.localService,
    isDefault: voice.default,
  };
}

const PITCH_RANGE = { min: 0.7, max: 1.35 };
const RATE_RANGE = { min: 0.9, max: 1.12 };

/**
 * Voices whose language matches the UI, most-preferred first. iOS ships a very
 * small inventory, which is why callers must not assume this is ever long.
 */
export function selectUsableVoices(voices: VoiceOption[], lang = 'en'): VoiceOption[] {
  const base = lang.split('-')[0]!.toLowerCase();
  const matching = voices.filter((v) => v.lang.toLowerCase().startsWith(base));
  const pool = matching.length > 0 ? matching : voices;
  return [...pool].sort((a, b) => {
    if (a.localService !== b.localService) return a.localService ? -1 : 1;
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

/** The subset of a character this module needs. */
export interface VoiceCharacter {
  id: string;
  gender?: CharacterGender;
}

/**
 * The Web Speech API never exposes a voice's gender, so it is read off the
 * name. Platforms either say so outright ("Google UK English Female") or use a
 * first name, hence the roster of the voices Windows, macOS and Chrome ship.
 */
const FEMALE_VOICE_NAMES = new Set([
  'allison', 'alice', 'amelie', 'anna', 'aria', 'ava', 'carmit', 'catherine', 'clara', 'ellen',
  'emily', 'fiona', 'hazel', 'heather', 'heera', 'ioana', 'jenny', 'joana', 'karen', 'kate',
  'kyoko', 'laura', 'lekha', 'libby', 'linda', 'lucia', 'luciana', 'marie', 'mia', 'michelle',
  'milena', 'moira', 'monica', 'natasha', 'nicky', 'nora', 'olivia', 'paulina', 'samantha',
  'sara', 'serena', 'sonia', 'susan', 'tessa', 'veena', 'victoria', 'yuna', 'zira', 'zosia',
]);

const MALE_VOICE_NAMES = new Set([
  'aaron', 'alex', 'arthur', 'brandon', 'christopher', 'daniel', 'david', 'diego', 'eric', 'fred',
  'george', 'gordon', 'guy', 'james', 'jorge', 'juan', 'lee', 'luca', 'maged', 'mark', 'oliver',
  'otoya', 'ravi', 'rishi', 'roger', 'ryan', 'steffan', 'thomas', 'tom', 'xander', 'yuri',
]);

/** Best guess at a voice's gender, or null when the name gives nothing away. */
export function classifyVoiceGender(voice: VoiceOption): 'female' | 'male' | null {
  const name = voice.name.toLowerCase();
  if (/\b(female|woman)\b/.test(name)) return 'female';
  if (/\b(male|man)\b/.test(name)) return 'male';

  for (const word of name.split(/[^a-z]+/)) {
    if (FEMALE_VOICE_NAMES.has(word)) return 'female';
    if (MALE_VOICE_NAMES.has(word)) return 'male';
  }
  return null;
}

/**
 * Deterministically gives every character a distinct-sounding voice.
 *
 * Characters are grouped by gender so that a scene's women are told apart from
 * each other, not merely from its men. Distinct `voiceURI`s are handed out
 * first; once a group runs out — the normal case on iOS, which may expose only
 * one or two — its characters are separated by hash-derived pitch and rate.
 */
export function assignVoices(
  characters: VoiceCharacter[],
  voices: VoiceOption[],
  lang = 'en',
): Record<string, VoiceAssignment> {
  const pool = selectUsableVoices(voices, lang);
  const female = pool.filter((v) => classifyVoiceGender(v) === 'female');
  const male = pool.filter((v) => classifyVoiceGender(v) === 'male');

  const groups: Record<CharacterGender, VoiceCharacter[]> = { female: [], male: [], neutral: [] };
  for (const character of [...characters].sort((a, b) => a.id.localeCompare(b.id))) {
    groups[character.gender ?? 'neutral'].push(character);
  }

  const assignments: Record<string, VoiceAssignment> = {};

  for (const [gender, members] of Object.entries(groups) as [CharacterGender, VoiceCharacter[]][]) {
    const preferred = gender === 'female' ? female : gender === 'male' ? male : [];
    const usable = preferred.length > 0 ? preferred : pool;

    members.forEach((character, index) => {
      const voice = usable.length > 0 ? usable[index % usable.length]! : null;
      const cycle = usable.length > 0 ? Math.floor(index / usable.length) : index;
      assignments[character.id] = {
        voiceURI: voice?.voiceURI ?? null,
        // The first pass through the pool stays neutral; later passes are detuned.
        pitch: cycle === 0 ? 1 : derive(character.id, PITCH_RANGE, 'pitch'),
        rate: cycle === 0 ? 1 : derive(character.id, RATE_RANGE, 'rate'),
      };
    });
  }

  return assignments;
}

function derive(seed: string, range: { min: number; max: number }, salt: string): number {
  const fraction = (hashString(`${salt}:${seed}`) % 1000) / 1000;
  const value = range.min + fraction * (range.max - range.min);
  return Math.round(value * 100) / 100;
}

export const DEFAULT_VOICE: VoiceAssignment = { voiceURI: null, pitch: 1, rate: 1 };

export function resolveVoice(
  characterId: string,
  overrides: Record<string, VoiceAssignment>,
  generated: Record<string, VoiceAssignment>,
): VoiceAssignment {
  return overrides[characterId] ?? generated[characterId] ?? DEFAULT_VOICE;
}
